import React, { useState, useEffect } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
//@ts-ignore
import "cropperjs/dist/cropper.css";
import { getRequest, putWithFormData } from "../../helpers/api.helper";
import { toast } from "react-toastify";
import FullPageLoader from "../../components/FullPageLoader";
import { RiArrowRightSLine } from "react-icons/ri";
import {
  AiOutlineClose,
  AiOutlineEye,
  AiOutlineEyeInvisible,
} from "react-icons/ai";
import { DropdownOption } from "../../types";
import { PrimaryDropdownKey } from "../../types/user.creation";
import ProfileImageUpload from "../../components/ProfileImageUpload";
import { dropdownOptions } from "../../utils/user.creation.constants";
import ProfileImageCropModal from "../../components/ProfileImageCropModal";
import { formatCreatedBy } from "../../utils/createdBy";
import { getCountries, getCountryCallingCode } from "react-phone-number-input";

const Step1Edit = () => {
  const [isLoading, setLoading] = useState<boolean>(false);
  const [showPassword, setShowPassword] = useState(false);
  const [modalData, setModalData] = useState<{
    key: PrimaryDropdownKey;
    options: DropdownOption[];
  } | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [rawImage, setRawImage] = useState<File | null>(null);
  const [image, setImage] = useState<File | null>(null);

  const [showImageCrop, setShowImageCrop] = useState(false);
  const [pendingSubmit, setPendingSubmit] = useState(false);

  const [selectedOptions, setSelectedOptions] = useState<
    Record<PrimaryDropdownKey, DropdownOption>
  >({
    gender: { value: "", label: "Select Gender" },
    createdBy: { value: "self", label: "Self" },
    citizenship: { value: "Indian", label: "Indian Profile" },
  });

  const navigate = useNavigate();

  const { id } = useParams();
  const { state } = useLocation();
  const profile = state?.profile;

  const [basicForm, setBasicForm] = useState({
    fullName: "",
    countryCode: "+91",
    mobileNumber: "",
    email: "",
    password: "",
    createdBy: "self",
  });

  const [existingImage, setExistingImage] = useState<string | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);

  /* -----------------------------
     Load existing profile
  ------------------------------*/
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const profileId = id || profile?.martialId;
        if (!profileId) return;

        const res = await getRequest(`/bureau/profile/${profileId}`);
        if (res.success) {
          setExistingImage(res.data.image || null);
          setBasicForm({
            fullName: res.data.fullName || "",
            mobileNumber: res.data.mobileNumber || "",
            email: res.data.email || "",
            password: res.data.password,
            createdBy: res.data.createdBy,
            countryCode: res.data.countryCode || "+91",
          });
          if (res.data.gender) {
            setSelectedOptions((prev) => ({
              ...prev,
              gender: {
                value: res.data.gender,
                label:
                  res.data.gender.charAt(0).toUpperCase() +
                  res.data.gender.slice(1),
              },
            }));
          }
        }
      } catch {
        toast.error("Failed to load profile");
      }
    };

    fetchProfile();
  }, [id, profile]);

  /* -----------------------------
     Save
  ------------------------------*/
  const handleSave = async () => {
    const profileId = id || profile?.martialId;
    if (!profileId) return toast.error("Profile id missing");

    if (!basicForm.fullName.trim()) {
      return toast.error("Please enter full name");
    }

    if (!basicForm.mobileNumber || basicForm.mobileNumber.length !== 10) {
      return toast.error("Enter valid mobile number");
    }
    if (rawImage && !image) {
      setPendingSubmit(true);
      setShowImageCrop(true);
      return;
    }

    setLoading(true);

    try {
      const payload = new FormData();
      payload.append("fullName", basicForm.fullName);
      payload.append("countryCode", basicForm.countryCode);
      payload.append("mobileNumber", basicForm.mobileNumber);
      payload.append("email", basicForm.email);
      payload.append("gender", selectedOptions.gender.value);
      payload.append("citizenship", selectedOptions.citizenship.value);
      if (basicForm.password) {
        payload.append("password", basicForm.password);
      }

      if (image) {
        payload.append("image", image);
      }

      payload.append("step1", "1");

      const res = await putWithFormData(
        `/bureau/profile/${profile._id}`,
        payload,
      );

      if (res.success) {
        toast.success("Profile updated successfully");
        navigate(-1);
      } else {
        toast.error(res.message || "Update failed");
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    return () => {
      if (imagePreview) URL.revokeObjectURL(imagePreview);
    };
  }, [imagePreview]);

  const openModal = (key: PrimaryDropdownKey) => {
    setModalData({ key, options: dropdownOptions[key] });
  };

  const closeModal = () => {
    setModalData(null);
    setSearchTerm("");
  };

  const handleSelect = (selectedOption: any) => {
    // @ts-ignore
    setSelectedOptions({ ...selectedOptions, [modalData.key]: selectedOption });
    closeModal();
  };

  return (
    <>
      {/* Top Header */}
      <div className="fixed top-0 left-0 right-0 z-40 bg-white border-b border-gray-200">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center justify-center h-9 w-9 rounded-full hover:bg-gray-100 active:bg-gray-200"
            aria-label="Go back"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-5 w-5 text-gray-700"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={2}
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M15 19l-7-7 7-7"
              />
            </svg>
          </button>

          <h1 className="text-base sm:text-lg font-semibold text-gray-800">
            Basic Information
          </h1>
        </div>
      </div>

      {isLoading && <FullPageLoader />}
      <div className="profile-edit-page pt-20 p-6 md:p-8">
        <ProfileImageUpload
          existingImage={existingImage}
          setImage={(file) => {
            setRawImage(file);
            setShowImageCrop(true);
          }}
        />

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Full Name *
          </label>
          <input
            type="text"
            value={basicForm.fullName}
            onChange={(e) =>
              setBasicForm({
                ...basicForm,
                fullName: e.target.value.replace(/[^a-zA-Z\s]/g, ""),
              })
            }
            className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            required
          />
        </div>
        {(Object.keys(dropdownOptions) as PrimaryDropdownKey[]).map((key) => {
          // if (whose == "mediator" && key == "createdBy") return null;
          return (
            <div key={key} className="mt-4">
              <label className="block text-gray-800 font-semibold mb-1 capitalize">
                {key.replace(/_/g, " ")}
              </label>
              <div
                onClick={() => openModal(key)}
                className="flex items-center justify-between px-4 py-3 border rounded-lg cursor-pointer hover:shadow-md transition-all bg-gray-50 hover:bg-gray-100"
              >
                <span className="text-gray-700">
                  {(key === "createdBy"
                    ? formatCreatedBy(selectedOptions[key]?.value)
                    : selectedOptions[key]?.label) || `Select ${key}`}
                </span>
                <RiArrowRightSLine className="text-2xl text-gray-500" />
              </div>
            </div>
          );
        })}

        {/* Contact Number */}
        <div className="mt-4">
          <label className="block text-gray-800 font-semibold mb-1">
            Contact Number
          </label>
          <div className="flex gap-3">
            <select
              value={basicForm.countryCode}
              onChange={(e) =>
                setBasicForm({
                  ...basicForm,
                  countryCode: e.target.value,
                })
              }
              className="border border-gray-300 rounded-lg px-2 py-2 bg-white focus:ring-2 focus:ring-red-400"
            >
              {getCountries().map((country) => (
                <option
                  key={country}
                  value={`+${getCountryCallingCode(country)}`}
                >
                  +{getCountryCallingCode(country)}
                </option>
              ))}
            </select>
            <input
              type="tel"
              placeholder="Ex: 98XXXXXXXX"
              value={basicForm.mobileNumber}
              onChange={(e) =>
                setBasicForm({
                  ...basicForm,
                  mobileNumber: e.target.value.replace(/\D/g, ""),
                })
              }
              className="flex-1 border border-gray-300 rounded-lg px-4 py-2 focus:ring-2 focus:ring-red-400"
              maxLength={10}
            />
          </div>
        </div>

        {/* Password */}
        <div className="mt-4">
          <label className="block text-gray-800 font-semibold mb-1">
            Password
          </label>
          <div className="relative">
            <input
              type={showPassword ? "text" : "password"}
              value={basicForm.password}
              className="w-full border border-gray-300 rounded-lg px-4 py-2 bg-gray-50 pr-10 focus:ring-2 focus:ring-red-400"
              onChange={(e) =>
                setBasicForm({
                  ...basicForm,
                  password: e.target.value,
                })
              }
            />
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-red-500"
            >
              {showPassword ? (
                <AiOutlineEyeInvisible size={20} />
              ) : (
                <AiOutlineEye size={20} />
              )}
            </button>
          </div>
        </div>

        {/* Terms */}

        {/* Action Buttons */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pt-6">
          <button
            type="button"
            onClick={handleSave}
            disabled={isLoading}
            className={`flex-1 px-6 py-3.5 rounded-lg font-semibold transition-all shadow-lg
      ${
        isLoading
          ? "bg-gray-300 text-gray-500 cursor-not-allowed"
          : "bg-gradient-to-r from-pink-500 to-purple-600 text-white hover:from-pink-600 hover:to-purple-700"
      }`}
          >
            {isLoading ? "Submitting..." : "Submit"}
          </button>
        </div>

        {modalData && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex justify-center items-center z-50">
            <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-6 relative">
              <button
                className="absolute top-3 right-3 text-gray-500 hover:text-red-500"
                onClick={() => {
                  setModalData(null);
                  setSearchTerm("");
                }}
              >
                <AiOutlineClose size={22} />
              </button>
              <h2 className="text-lg font-bold mb-4 text-center capitalize">
                Select {modalData.key.replace(/_/g, " ")}
              </h2>
              <input
                type="text"
                placeholder={`Search ${modalData.key}`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg mb-4 focus:ring-2 focus:ring-red-400"
              />
              <div className="max-h-64 overflow-y-auto divide-y">
                {modalData.options
                  .filter((option) =>
                    option.label
                      .toLowerCase()
                      .includes(searchTerm.toLowerCase()),
                  )
                  .map((option, index) => (
                    <button
                      key={option.value}
                      onClick={() => handleSelect(option)}
                      className="block w-full text-left px-4 py-2 hover:bg-red-50 transition"
                    >
                      {option.label}
                    </button>
                  ))}
              </div>
            </div>
          </div>
        )}
      </div>
      {showImageCrop && rawImage && (
        <ProfileImageCropModal
          sourceFile={rawImage}
          onConfirm={(croppedFile: File) => {
            setImage(croppedFile);
            setImagePreview(URL.createObjectURL(croppedFile));
            setShowImageCrop(false);

            if (pendingSubmit) {
              setPendingSubmit(false);
              handleSave();
            }
          }}
          onClose={() => {
            setShowImageCrop(false);
            setPendingSubmit(false);
          }}
        />
      )}
    </>
  );
};

export default Step1Edit;
